import {
  LineaDte,
  MontosDeLinea,
  TipoMontoLinea,
  aMonto,
  ivaSobreBase,
  montosDeLinea,
  redondear2,
  totalesDelDocumento,
} from './calculo-dte'

export const TASA_RETENCION_RENTA = 0.1
export const DECIMALES_TRIBUTOS = 2

export type ProductoDocumento = {
  precio?: any
  cantidad?: any
  conIva?: any
  tipoDescuento?: any
  descuento?: any
  exento?: any
  noSujeto?: any
  noGravado?: any
  servicio?: any
}

export interface OpcionesLineaDocumento {
  sinIva?: boolean
  otrosImpuestosDeLinea?: (venta: number) => number
  otrosImpuestosEnTotal?: boolean
}

export interface CalculoDeLinea {
  precioUni: number
  neto: number
  descuento: number
  subtotal: number
  iva: number
  otrosImpuestos: number
  total: number
}

export interface OpcionesTotalesDocumento {
  aplicaPercepcion?: boolean
  aplicaRetencion?: boolean
  aplicaRetencionRenta?: boolean
  decimalesTributos?: number
  otrosImpuestosDeLinea?: (producto: ProductoDocumento, venta: number) => number
  resumenTributosDeLinea?: (
    resumenActual: Array<any>,
    producto: ProductoDocumento,
    venta: number
  ) => Array<any>
}

export interface TotalesDocumento {
  lineas: MontosDeLinea[]
  sumaOperaciones: number
  totalGravada: number
  totalExenta: number
  totalNoSuj: number
  totalNoGravado: number
  descuentoTotal: number
  iva: number
  ivaPercibido: number
  ivaRetenido: number
  retencionRenta: number
  otrosImpuestos: number
  subtotal: number
  total: number
  resumenTributos: Array<any>
}

export const tipoMontoDelProducto = (producto: ProductoDocumento): TipoMontoLinea =>
  producto?.noGravado
    ? 'noGravado'
    : producto?.exento
    ? 'exento'
    : producto?.noSujeto
    ? 'noSujeto'
    : 'gravado'

export const comoLineaDte = (
  producto: ProductoDocumento,
  opciones: OpcionesLineaDocumento = {}
): LineaDte => ({
  precio: producto?.precio ?? '0',
  cantidad: producto?.cantidad ?? '0',
  conIva: opciones.sinIva ? false : producto?.conIva,
  tipoDescuento: producto?.tipoDescuento,
  descuento: producto?.descuento,
  tipoMonto: tipoMontoDelProducto(producto),
})

export const montosDeProducto = (
  producto: ProductoDocumento,
  opciones: OpcionesLineaDocumento = {}
): MontosDeLinea => montosDeLinea(comoLineaDte(producto, opciones))

export const calculoDeLinea = (
  producto: ProductoDocumento,
  opciones: OpcionesLineaDocumento = {}
): CalculoDeLinea => {
  const {precioUni, descuento, venta, ventaExacta, tipoMonto} = montosDeProducto(producto, opciones)
  const otrosImpuestos = redondear2(opciones.otrosImpuestosDeLinea?.(venta) ?? 0)
  const iva = opciones.sinIva || tipoMonto !== 'gravado' ? 0 : ivaSobreBase(ventaExacta)

  return {
    precioUni,
    neto: redondear2(precioUni * aMonto(producto?.cantidad)),
    descuento,
    subtotal: venta,
    iva,
    otrosImpuestos,
    total: redondear2(venta + iva + (opciones.otrosImpuestosEnTotal ? otrosImpuestos : 0)),
  }
}

export const totalesDeDocumento = (
  productos: ProductoDocumento[],
  opciones: OpcionesTotalesDocumento = {}
): TotalesDocumento => {
  const totales = totalesDelDocumento(
    productos.map((producto) => comoLineaDte(producto)),
    {aplicaPercepcion: opciones.aplicaPercepcion, aplicaRetencion: opciones.aplicaRetencion}
  )

  let otrosImpuestos = 0
  let retencionRenta = 0
  let resumenTributos: Array<any> = []

  productos.forEach((producto, indice) => {
    const {venta, tipoMonto} = totales.lineas[indice]
    if (tipoMonto === 'noGravado') return

    otrosImpuestos = redondear2(
      otrosImpuestos + (opciones.otrosImpuestosDeLinea?.(producto, venta) ?? 0)
    )

    if (opciones.resumenTributosDeLinea) {
      resumenTributos = opciones.resumenTributosDeLinea(resumenTributos, producto, venta)
    }

    if (opciones.aplicaRetencionRenta && producto?.servicio) {
      retencionRenta = redondear2(retencionRenta + redondear2(venta * TASA_RETENCION_RENTA))
    }
  })

  const decimales = opciones.decimalesTributos ?? DECIMALES_TRIBUTOS
  resumenTributos.forEach((tributo) => {
    tributo.valor = parseFloat(aMonto(tributo.valor).toFixed(decimales))
  })

  const subtotal = redondear2(totales.subTotal + otrosImpuestos)
  const total = redondear2(
    subtotal + totales.ivaPercibido - totales.ivaRetenido - retencionRenta + totales.totalNoGravado
  )

  return {
    lineas: totales.lineas,
    sumaOperaciones: totales.sumaOperaciones,
    totalGravada: totales.totalGravada,
    totalExenta: totales.totalExenta,
    totalNoSuj: totales.totalNoSuj,
    totalNoGravado: totales.totalNoGravado,
    descuentoTotal: totales.descuentoTotal,
    iva: totales.iva,
    ivaPercibido: totales.ivaPercibido,
    ivaRetenido: totales.ivaRetenido,
    retencionRenta,
    otrosImpuestos,
    subtotal,
    total,
    resumenTributos,
  }
}
